import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowRight, Loader2, ShieldCheck, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from '../hooks/use-toast';
import { authApi, saveAuth } from '../lib/api';

/**
 * Two-step Email OTP flow used by both Login and Signup pages.
 * Props:
 *  - mode: 'login' | 'signup'
 *  - defaults?: { name, company, role, city } — pre-fill for signup context
 *  - navigateTo: string — where to route after success (default: '/app/dashboard')
 */
export default function EmailOtpFlow({ mode = 'login', defaults = {}, navigateTo = '/app/dashboard' }) {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // 'email' | 'otp'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [demoOtp, setDemoOtp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [profile, setProfile] = useState({
    name: defaults.name || '',
    company: defaults.company || '',
    role: defaults.role || 'shipper',
    city: defaults.city || '',
  });
  const cdTimer = useRef(null);

  const startCooldown = (sec = 30) => {
    setCooldown(sec);
    if (cdTimer.current) clearInterval(cdTimer.current);
    cdTimer.current = setInterval(() => {
      setCooldown(c => {
        if (c <= 1) { clearInterval(cdTimer.current); return 0; }
        return c - 1;
      });
    }, 1000);
  };
  useEffect(() => () => cdTimer.current && clearInterval(cdTimer.current), []);

  const sendOtp = async (isResend = false) => {
    if (!email || !/^[^@]+@[^@]+\.[^@]+$/.test(email)) {
      toast({ title: 'Invalid email', description: 'Please enter a valid email address.' });
      return;
    }
    isResend ? setResending(true) : setLoading(true);
    try {
      const r = await authApi.sendEmailOtp(email);
      if (r.demo_otp) {
        setDemoOtp(r.demo_otp);
        toast({ title: 'Demo mode', description: `OTP: ${r.demo_otp} (Resend key not configured, showing here for testing)` });
      } else {
        setDemoOtp(null);
        toast({ title: 'Check your email', description: r.message || 'OTP sent. It may take up to 30 seconds.' });
      }
      setStep('otp');
      startCooldown(30);
    } catch (err) {
      toast({ title: 'Could not send OTP', description: err?.response?.data?.detail || 'Please try again.' });
    } finally {
      setLoading(false); setResending(false);
    }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault?.();
    if (!/^\d{6}$/.test(otp)) {
      toast({ title: 'Invalid code', description: 'Enter the 6-digit OTP from your email.' });
      return;
    }
    setLoading(true);
    try {
      const payload = { email, otp };
      if (mode === 'signup') {
        payload.name = profile.name || undefined;
        payload.company = profile.company || undefined;
        payload.role = profile.role || 'shipper';
        payload.city = profile.city || undefined;
      }
      const { token, user } = await authApi.verifyEmailOtp(payload);
      saveAuth(token, user);
      toast({ title: mode === 'signup' ? 'Welcome to EZLOAD!' : 'Signed in!', description: user.name });
      navigate(navigateTo);
    } catch (err) {
      toast({ title: 'Verification failed', description: err?.response?.data?.detail || 'Invalid or expired OTP.' });
    } finally {
      setLoading(false);
    }
  };

  if (step === 'email') {
    return (
      <div className="space-y-4">
        {mode === 'signup' && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Full name <span className="text-gray-400 text-[10px]">optional</span></Label>
                <Input data-testid="email-signup-name" placeholder="Rajesh Kumar" className="h-11 mt-1.5" value={profile.name} onChange={e => setProfile({ ...profile, name: e.target.value })} />
              </div>
              <div>
                <Label className="text-sm">City <span className="text-gray-400 text-[10px]">optional</span></Label>
                <Input data-testid="email-signup-city" placeholder="Mumbai" className="h-11 mt-1.5" value={profile.city} onChange={e => setProfile({ ...profile, city: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-sm">Company / Fleet name <span className="text-gray-400 text-[10px]">optional</span></Label>
              <Input data-testid="email-signup-company" placeholder="Kumar Transports Pvt. Ltd." className="h-11 mt-1.5" value={profile.company} onChange={e => setProfile({ ...profile, company: e.target.value })} />
            </div>
          </>
        )}
        <div>
          <Label className="text-sm font-medium">Email address</Label>
          <div className="relative mt-1.5">
            <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="email-otp-input"
              type="email"
              autoComplete="email"
              required
              placeholder="you@company.in"
              className="h-11 pl-9"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendOtp()}
            />
          </div>
        </div>
        <Button
          data-testid="email-send-otp-btn"
          type="button"
          onClick={() => sendOtp(false)}
          disabled={loading}
          className="w-full h-11 bg-[#00A651] hover:bg-[#008a43] text-white"
        >
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending code…</> : <>Send verification code <ArrowRight className="w-4 h-4 ml-2" /></>}
        </Button>
        <p className="text-[11px] text-gray-500 text-center leading-relaxed">
          We'll send a 6-digit code to your email. No password needed.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={verifyOtp} className="space-y-4">
      <div className="flex items-center gap-2 p-3 rounded-lg bg-[#00A651]/5 border border-[#00A651]/20">
        <ShieldCheck className="w-5 h-5 text-[#00A651] flex-shrink-0" />
        <div className="text-xs text-gray-700 flex-1">
          Code sent to <span className="font-semibold text-[#0B2545]">{email}</span>
          <button type="button" onClick={() => setStep('email')} className="ml-2 text-[#00A651] font-medium underline">Change</button>
        </div>
      </div>
      {demoOtp && (
        <div data-testid="email-demo-otp-banner" className="p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-800">
          <b>Demo mode:</b> Your OTP is <span className="font-mono text-sm font-bold">{demoOtp}</span> (real email delivery activates once RESEND_API_KEY is configured).
        </div>
      )}
      <div>
        <Label className="text-sm font-medium">6-digit code</Label>
        <Input
          data-testid="email-otp-code-input"
          inputMode="numeric"
          pattern="\d{6}"
          maxLength={6}
          required
          autoFocus
          placeholder="123456"
          className="h-14 mt-1.5 text-center text-2xl tracking-[0.5em] font-mono font-bold"
          value={otp}
          onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
        />
      </div>
      <Button
        data-testid="email-verify-otp-btn"
        type="submit"
        disabled={loading || otp.length !== 6}
        className="w-full h-11 bg-[#00A651] hover:bg-[#008a43] text-white"
      >
        {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying…</> : <>Verify & continue <ArrowRight className="w-4 h-4 ml-2" /></>}
      </Button>
      <div className="flex items-center justify-center text-xs text-gray-500">
        {cooldown > 0 ? (
          <span>Resend code in <b>{cooldown}s</b></span>
        ) : (
          <button
            data-testid="email-resend-otp-btn"
            type="button"
            onClick={() => sendOtp(true)}
            disabled={resending}
            className="text-[#00A651] font-semibold hover:underline flex items-center gap-1"
          >
            {resending ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Resend code
          </button>
        )}
      </div>
    </form>
  );
}
